import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { LogOut, User as UserIcon, Menu } from 'lucide-react';
import { CapinLogo } from '@/components/brand/CapinLogo';
import { SidebarContent } from './Sidebar';

const roleLabel = {
  owner: 'Owner',
  consultant: 'Consultant',
  tenant: 'Tenant',
  agent: 'Agent',
  admin: 'Super Admin',
};

export function Topbar() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!user) return null;
  const initials = (user.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const showHamburger = user.role !== 'tenant'; // tenant uses bottom nav on mobile

  return (
    <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-6 lg:px-8 h-14">
        {/* Left: hamburger (mobile only) + brand (mobile) / welcome (desktop) */}
        <div className="flex items-center gap-2 min-w-0">
          {showHamburger && (
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-11 w-11"
                  aria-label="Open navigation menu"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="p-0 w-[280px] max-w-[85vw] bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] border-r flex flex-col"
              >
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarContent
                  role={user.role}
                  branding={branding}
                  onNavigate={() => setSheetOpen(false)}
                />
              </SheetContent>
            </Sheet>
          )}

          {/* Mobile brand (visible <md) */}
          <div className="flex md:hidden items-center gap-2 min-w-0">
            <CapinLogo size={32} monogram={branding.logo_monogram} imageUrl={branding.logo_url} />
            <div className="text-sm font-semibold truncate">
              {branding.brand_name}
              <span style={{ color: '#d4a017' }}> {branding.brand_suffix}</span>
            </div>
          </div>

          {/* Desktop welcome (visible md+) */}
          <div className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Welcome,</span>
            <span className="font-medium truncate max-w-[240px]">{user.name}</span>
          </div>
        </div>

        {/* Right: role badge + profile menu */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium bg-[hsl(var(--role-accent)/0.10)] text-[hsl(var(--role-accent))] border-[hsl(var(--role-accent)/0.25)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--role-accent))]" />
            {roleLabel[user.role]}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="px-1.5 h-11 w-11" data-testid="topbar-profile-menu" aria-label="Account menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[hsl(var(--role-accent))] text-white text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium truncate">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                <div className="sm:hidden mt-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium bg-[hsl(var(--role-accent)/0.10)] text-[hsl(var(--role-accent))] border-[hsl(var(--role-accent)/0.25)]">
                    {roleLabel[user.role]}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <UserIcon className="mr-2 h-4 w-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
